import React, { useEffect, useRef, useState } from "react";
import About from "./About";
import NewAlbert from "./NewAlbert";
import AIKnowledge from "./AIKnowledge";
import Contact from "./Contact";
import { optimizeImageUrl } from "../lib/utils";

export default function Home({
  lang,
  t,
  galleries,
  onImageClick,
  activeSection,
  setActiveSection,
  scrollToSection,
  isScrollingRef,
}) {
  const containerRef = useRef(null);
  const [activeSlide, setActiveSlide] = useState(0); // 0 or 1 for the two pairs of images

  // Find the home-hero gallery if available, otherwise use fallback URLs
  const heroGallery = galleries.find(
    (g) =>
      g.slug === "home-hero" ||
      g.slug?.en === "home-hero" ||
      g.slug?.da === "klima-og-teknologiudstilling"
  );
  const heroImages = heroGallery?.images || [
    "https://assets.auxodev.dk/assets/25c88f45-a091-4778-ada1-e41c6b1f66ac/copenhagen-nyhavn.jpg",
    "https://assets.auxodev.dk/assets/25c88f45-a091-4778-ada1-e41c6b1f66ac/shutterstock-2750147293.jpg",
    "https://assets.auxodev.dk/assets/25c88f45-a091-4778-ada1-e41c6b1f66ac/ai-representation.jpg",
    "https://assets.auxodev.dk/assets/25c88f45-a091-4778-ada1-e41c6b1f66ac/global-work.jpg",
  ];

  // Auto-play slider every 5 seconds
  useEffect(() => {
    const timer = setInterval(() => {
      setActiveSlide((prev) => (prev === 0 ? 1 : 0));
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  // Intersection Observer to detect active section
  useEffect(() => {
    const observerOptions = {
      root: null, // use viewport
      rootMargin: "-10% 0px -10% 0px", // trigger slightly before section fills screen
      threshold: 0.4, // trigger when 40% of section is visible
    };

    const observerCallback = (entries) => {
      if (isScrollingRef.current) return;

      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const sectionId = entry.target.id;
          setActiveSection(sectionId);
          window.history.replaceState(null, null, `#${sectionId}`);
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, observerOptions);
    const sectionIds = ["home", "about", "newalbert", "ai-knowledge", "contact"];

    sectionIds.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });

    return () => {
      observer.disconnect();
    };
  }, [setActiveSection, isScrollingRef]);

  // Initial Hash Scroll
  useEffect(() => {
    const hash = window.location.hash;
    if (hash) {
      const targetId = hash.replace("#", "");
      const element = document.getElementById(targetId);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: "smooth" });
          setActiveSection(targetId);
        }, 300);
      }
    }
  }, [setActiveSection]);

  return (
    <div
      ref={containerRef}
      className="w-full scroll-smooth"
    >
      {/* 1. HERO SECTION (#home) */}
      <section
        id="home"
        className="relative w-full min-h-[calc(100vh-73px)] bg-brand-navy overflow-hidden flex items-center justify-center py-12 md:py-20"
      >
        {/* Hero Slider Container */}
        <div className="absolute inset-x-0 top-0 w-full h-[55vh] sm:h-[65vh] md:h-[75vh] lg:h-[80vh] overflow-hidden">
          {/* Slide 0 */}
          <div
            className={`absolute inset-0 grid grid-cols-1 sm:grid-cols-2 w-full h-full transition-all duration-1000 ease-in-out ${
              activeSlide === 0 ? "opacity-100 scale-100 z-10" : "opacity-0 scale-95 z-0 pointer-events-none"
            }`}
          >
            <div className="h-full border-r border-white/10 relative group overflow-hidden">
              <img
                src={optimizeImageUrl(heroImages[0], 1000, 85)}
                alt="Nyhavn, København forskningsmiljø"
                width="1000"
                height="700"
                loading="eager"
                fetchPriority="high"
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-white/5 group-hover:bg-transparent transition-colors duration-300" />
            </div>
            <div className="hidden sm:block h-full relative group overflow-hidden">
              <img
                src={optimizeImageUrl(heroImages[1], 1000, 85)}
                alt="Havvindmøllepark bæredygtighed"
                width="1000"
                height="700"
                loading="eager"
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-white/5 group-hover:bg-transparent transition-colors duration-300" />
            </div>
          </div>

          {/* Slide 1 */}
          <div
            className={`absolute inset-0 grid grid-cols-1 sm:grid-cols-2 w-full h-full transition-all duration-1000 ease-in-out ${
              activeSlide === 1 ? "opacity-100 scale-100 z-10" : "opacity-0 scale-95 z-0 pointer-events-none"
            }`}
          >
            <div className="h-full border-r border-white/10 relative group overflow-hidden">
              <img
                src={optimizeImageUrl(heroImages[2], 1000, 85)}
                alt="Kunstig intelligens og vidensgraf repræsentation"
                width="1000"
                height="700"
                loading="lazy"
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-white/5 group-hover:bg-transparent transition-colors duration-300" />
            </div>
            <div className="hidden sm:block h-full relative group overflow-hidden">
              <img
                src={optimizeImageUrl(heroImages[3], 1000, 85)}
                alt="Globalt akademisk forskningssamarbejde"
                width="1000"
                height="700"
                loading="lazy"
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-white/5 group-hover:bg-transparent transition-colors duration-300" />
            </div>
          </div>

          {/* Slider Arrow Buttons */}
          <button
            onClick={() => setActiveSlide((prev) => (prev === 0 ? 1 : 0))}
            className="absolute left-4 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-black/20 hover:bg-black/40 text-white flex items-center justify-center transition-all duration-200 backdrop-blur-sm cursor-pointer"
            aria-label="Forrige slide"
          >
            <i className="fa-solid fa-chevron-left text-xs" aria-hidden="true" />
          </button>
          <button
            onClick={() => setActiveSlide((prev) => (prev === 0 ? 1 : 0))}
            className="absolute right-4 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-black/20 hover:bg-black/40 text-white flex items-center justify-center transition-all duration-200 backdrop-blur-sm cursor-pointer"
            aria-label="Næste slide"
          >
            <i className="fa-solid fa-chevron-right text-xs" aria-hidden="true" />
          </button>
        </div>

        {/* Overlapping Centered Card */}
        <div className="relative z-10 w-full max-w-4xl px-6 translate-y-[100px] md:translate-y-[256px]">
          <div className="w-full bg-white/95 backdrop-blur-md pt-12 pb-8 px-6 md:p-10 shadow-2xl border-l-4 border-brand-orange border-y border-r border-zinc-200/80">
            <div className="text-center md:text-left max-w-3xl mx-auto">
              {/* Subtitle */}
              <span className="text-xs font-bold text-brand-orange uppercase tracking-[0.25em] block mb-2">
                AI-POWERED KNOWLEDGE MANAGEMENT PLATFORMS
              </span>
              {/* Title - Primary Page H1 */}
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-brand-navy tracking-tight mb-3 font-display">
                {t("home.heroTitle")}
              </h1>
              {/* Tagline */}
              <p className="text-sm md:text-base text-zinc-700 font-light leading-relaxed mb-5">
                {t("home.heroTagline")}
              </p>
              {/* CTA */}
              <div className="flex justify-center md:justify-start">
                <button
                  onClick={() => scrollToSection("about")}
                  className="inline-flex items-center gap-2 text-xs font-bold text-brand-blue hover:text-brand-navy transition-colors duration-200 uppercase tracking-wider cursor-pointer"
                  aria-label="Læs mere om vores mission"
                >
                  <span>Learn more about our mission</span>
                  <i className="fa-solid fa-arrow-down text-xs animate-bounce" aria-hidden="true" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ABOUT SECTION (#about) */}
      <About lang={lang} t={t} />

      {/* NEWALBERT SECTION (#newalbert) */}
      <NewAlbert lang={lang} t={t} />

      {/* AI KNOWLEDGE SECTION (#ai-knowledge) */}
      <AIKnowledge lang={lang} t={t} />

      {/* CONTACT SECTION (#contact) */}
      <Contact lang={lang} t={t} />
    </div>
  );
}
